import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import {
  CustomerCheckIn,
  CheckInEntryMethod, Customer
} from '../../../../../../../models/customer';
import {
  dateStringFromISO8601,
  timeStringFromISO8601
} from '../../../../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CheckInAlertsModalComponent } from './modals/check-in-alerts-modal/check-in-alerts-modal.component';
import { CheckInNoEntryMethodModalComponent } from './modals/check-in-no-entry-method-modal/check-in-no-entry-method-modal.component';
import {
  ChooseCheckInSharedPassModalComponent
} from './modals/choose-check-in-shared-pass-modal/choose-check-in-shared-pass-modal.component';
import { ChooseCheckInGuestPassModalComponent } from './modals/choose-check-in-guest-pass-modal/choose-check-in-guest-pass-modal.component';
import { CheckInDetailModalComponent } from './modals/check-in-detail-modal/check-in-detail-modal.component';
import { LocalStorage } from 'angular-web-storage';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ListResponse } from '../../../../../../../models/list-response';
import { CheckInMissingProficiency, StaffCheckInResult } from '../../../../../../../models/check-in';
import { getCustomerNameString, getMissingProficiencyWarningText } from '../../../../../../../utils-models';
import { CheckInService } from '../../../../../../../services/check-in/check-in.service';
import {
  CheckInNotifierService
} from '../../../../../../../services/check-in/check-in-notifier.service';

@Component({
  selector: 'app-check-ins',
  templateUrl: './check-ins-sub-list.component.html',
  styleUrls: ['./check-ins-sub-list.component.css']
})
export class CheckInsSubListComponent implements OnInit {

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeCustomerCheckInsSubList;

  public filtersChangedSubject = new Subject<null>();

  public customerId: number;

  public customer: Customer;
  public checkInsList: ListResponse<CustomerCheckIn>;
  public loadingCustomer = false;
  public loadingCheckIns = false;
  public loadingCheckInRequest = false;

  public customerNotFound = false;
  public serverError: number = null;

  public successMessage = '';
  public errorMessage = '';
  // PRO-1485: shown beside the success message when the customer just checked
  // in without holding the location's required proficiencies. Informational
  // only; the check-in has already completed.
  public checkInWarningMessage = '';
  public checkInWarningProficiencies: CheckInMissingProficiency[] = [];

  // Make relevant enums and library functions available to the template.
  public CheckInEntryMethod = CheckInEntryMethod;
  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private checkInService: CheckInService,
    private checkInNotifierService: CheckInNotifierService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    if (!this.pageSizeCustomerCheckInsSubList) {
      this.pageSizeCustomerCheckInsSubList = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomerCheckInsSubList = this.pageSizeCustomerCheckInsSubList;

    // Handle changes to the filters by reloading the list, subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCheckIns();
    });

    this.loadCustomer();
    this.loadCheckIns();
  }

  public checkInCustomer(bypassAlerts: boolean, freeEntryReason?: string): Promise<void> {
    this.loadingCheckInRequest = true;
    this.successMessage = '';
    this.errorMessage = '';
    this.clearCheckInWarning();

    return this.customerService
      .checkInCustomer(this.customerId, bypassAlerts, freeEntryReason)
      .then(result => {
        this.handleSuccessfulCheckIn(result);
      })
      .catch(reason => {
        const errorType = reason.error.error;
        if (errorType === 'check_in_alerts') {
          const checkInAlerts = reason.error.alerts;
          const modalRef = this.modalService.open(CheckInAlertsModalComponent);
          modalRef.componentInstance.alerts = checkInAlerts;
          modalRef.componentInstance.checkInAnywayCallback = () => {
            this.checkInCustomer(true, freeEntryReason);
          };
        }
        else if (errorType === 'no_entry_method') {
          const modalRef = this.modalService.open(CheckInNoEntryMethodModalComponent);
          modalRef.componentInstance.customerId = this.customerId;
          modalRef.componentInstance.provideFreeEntryCallback = (thisFreeEntryReason: string) => {
            this.checkInCustomer(bypassAlerts, thisFreeEntryReason);
          };
        }
      })
      .finally(() => { this.loadingCheckInRequest = false; });
  }

  /**
   * Initialize the process of checking in a customer via a shared pass: open
   * the modal allowing the user to choose the pass to use, then once a pass is
   * chosen, call checkInCustomerSharedPass to carry out the check-in.
   */
  public checkInCustomerSharedPassInit() {
    const choosePassModalRef = this.modalService.open(ChooseCheckInSharedPassModalComponent);
    choosePassModalRef.componentInstance.passChosenCallback = (passId: number) => {
      this.checkInCustomerSharedPass(passId, false);
    };
  }

  /**
   * Initialize the process of checking in a customer via a guest pass: open
   * the modal allowing the user to choose the customer whose guest pass will be
   * used, then once a customer is chosen, call checkInCustomerGuestPass to
   * carry out the check-in.
   */
  public checkInCustomerGuestPassInit() {
    const modalRef = this.modalService.open(ChooseCheckInGuestPassModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.guestPassChosenCallback = (guestPassId: number) => {
      this.checkInCustomerGuestPass(guestPassId, false);
    };
  }

  public openCheckInDetailModal(checkIn: CustomerCheckIn) {
    const modalRef = this.modalService.open(CheckInDetailModalComponent);
    modalRef.componentInstance.checkInId = checkIn.id;
    modalRef.componentInstance.checkInEditedCallback = () => {
      this.loadCheckIns();
    };
  }

  public loading() {
    return (
      this.loadingCustomer ||
      this.loadingCheckIns ||
      this.loadingCheckInRequest
    );
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.checkInsList) { return 1; }
    return Math.ceil(this.checkInsList.count / this.pageSizeCustomerCheckInsSubList);
  }

  private loadCustomer() {
    this.loadingCustomer = true;

    this.customerService
      .getCustomer(this.customerId)
      .then(customer => {
        this.customer = customer;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer = false; });
  }

  private loadCheckIns() {
    this.loadingCheckIns = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerCheckIns(
        this.customerId,
        this.pageNumber,
        this.pageSizeCustomerCheckInsSubList
      )
      .then(results => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.checkInsList = results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingCheckIns = false;
        }
      });
  }

  /**
   * Check in the customer via the given pass. If alerts are not bypassed, this
   * may fail if the customer has account alerts, in which case open a modal to
   * confirm bypassing the alerts, and if the bypass is confirmed, call this
   * function again but with the bypass flag set to true.
   */
  private checkInCustomerSharedPass(passId: number, bypassAlerts: boolean) {
    this.loadingCheckInRequest = true;
    this.successMessage = '';
    this.errorMessage = '';
    this.clearCheckInWarning();

    this.customerService
        .checkInCustomerViaEntryPass(this.customerId, passId, bypassAlerts)
      .then(result => {
        this.handleSuccessfulCheckIn(result);
      })
      .catch(reason => {
        const errorType = reason.error.error;
        if (errorType === 'check_in_alerts') {
          const checkInAlerts = reason.error.alerts;
          const modalRef = this.modalService.open(CheckInAlertsModalComponent);
          modalRef.componentInstance.alerts = checkInAlerts;
          modalRef.componentInstance.checkInAnywayCallback = () => {
            this.checkInCustomerSharedPass(passId, true);
          };
        }
      })
      .finally(() => { this.loadingCheckInRequest = false; });
  }

  /**
   * Check in the customer via the given other customer's guest pass.
   *
   * If alerts are not bypassed, this may fail if the customer has account
   * alerts, in which case we will open a modal to confirm bypassing the alerts,
   * and if the bypass is confirmed, call this function again but with the
   * bypass flag set to true.
   */
  private checkInCustomerGuestPass(guestPassId: number, bypassAlerts: boolean) {
    this.loadingCheckInRequest = true;
    this.successMessage = '';
    this.errorMessage = '';
    this.clearCheckInWarning();

    this.customerService
      .checkInCustomerViaGuestPass(this.customerId, guestPassId, bypassAlerts)
      .then(result => {
        this.handleSuccessfulCheckIn(result);
      })
      .catch(reason => {
        if (reason.status === 400) {
          const errorType = reason.error.error;
          if (errorType === 'check_in_alerts') {
            const checkInAlerts = reason.error.alerts;
            const modalRef = this.modalService.open(CheckInAlertsModalComponent);
            modalRef.componentInstance.alerts = checkInAlerts;
            modalRef.componentInstance.checkInAnywayCallback = () => {
              this.checkInCustomerGuestPass(guestPassId, true);
            };
          } else {
            this.errorMessage = errorType;
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCheckInRequest = false; });
  }

  private clearCheckInWarning() {
    this.checkInWarningMessage = '';
    this.checkInWarningProficiencies = [];
  }

  private handleSuccessfulCheckIn(result: StaffCheckInResult) {
    const locationName = this.accountService.getDefaultLocation().name;
    this.successMessage = 'Customer checked in to ' + locationName + ' successfully.';

    // Tolerates a response from a server that predates the field.
    const missing = (result && result.missing_check_in_proficiencies) || [];
    if (missing.length > 0) {
      this.checkInWarningProficiencies = missing;
      this.checkInWarningMessage = getMissingProficiencyWarningText(
        getCustomerNameString(this.customer) || 'This customer',
        locationName,
        result.check_in_required_proficiencies_match_mode,
        missing.map(p => p.name)
      );
    }

    this.loadCheckIns();

    // Tell the profile this tab sits inside, so that the alerts a check-in
    // raises - the "awaiting prize" challenge banner above all - appear now
    // rather than when its poll next runs (PRO-1449).
    this.checkInNotifierService.notifyCustomerCheckedIn(this.customerId);

    // Make requests to load the check-in stats and facility check-ins in
    // order to refresh the cache.
    this.checkInService.getCheckInStats(
      this.accountService.getMyAccount().default_gym_id,
      true
    ).then();
    this.checkInService.getInFacilityCustomers(
      this.accountService.getMyAccount().default_gym_id,
      true
    ).then();
  }

}
